import { MALEK_CART_ITEMS_KEY } from "src/constants/storageKeys";
import { CartItemType } from "src/types/cart/CartItem";
import { getStorageItem, saveStorageItem } from "./storage";


/**
 * localStorage holds the **guest** cart only. Once a user is authenticated the
 * server cart is authoritative, and mirroring it locally is actively harmful:
 * if the session ends without an explicit logout (tab closed with "Remember Me"
 * off) the leftover copy would be re-read as the next visitor's guest cart.
 *
 * App.tsx flips this on auth changes; every write helper below is a no-op while
 * a user is signed in. Reads are unaffected, and `storageCartReset` always runs
 * so logout can still clear whatever is there.
 */
let cartStorageEnabled = true;

export const setCartStorageEnabled = (enabled: boolean) => {
    cartStorageEnabled = enabled;
};

export const isCartStorageEnabled = () => cartStorageEnabled;

export const storageAddCartItem = (item: CartItemType) => {
    if (!cartStorageEnabled) return;
    const cartItems = getStorageItem(MALEK_CART_ITEMS_KEY) || [];
    const itemExists = cartItems.some((itm: CartItemType) => itm.slug === item.slug);

    if (!itemExists) {
        saveStorageItem(MALEK_CART_ITEMS_KEY, [item, ...cartItems]);
    }
};

export const storageRemoveCartItem = (slug: string) => {
    if (!cartStorageEnabled) return;
    const cartItems = getStorageItem(MALEK_CART_ITEMS_KEY) || [];
    const updatedCartItems = cartItems.filter((item: CartItemType) => item.slug !== slug);
    saveStorageItem(MALEK_CART_ITEMS_KEY, updatedCartItems);
};

export const storageCartReset = () => {
    saveStorageItem(MALEK_CART_ITEMS_KEY, []);
};

export const storageUpdateCartItemQty = (slug: string, qty: number) => {
    if (!cartStorageEnabled) return;
    const cartItems = getStorageItem(MALEK_CART_ITEMS_KEY) || [];
    const updatedItems = cartItems.map((item: CartItemType) =>
        item.slug === slug ? { ...item, qty } : item
    );
    saveStorageItem(MALEK_CART_ITEMS_KEY, updatedItems);
};

// Replace the whole persisted guest cart. Used when reconciling against the
// authoritative server cart so Redux and localStorage never drift apart.
export const storageSetCartItems = (items: CartItemType[]) => {
    if (!cartStorageEnabled) return;
    saveStorageItem(MALEK_CART_ITEMS_KEY, items);
};

const toNullableNumber = (value: unknown): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

// Normalise the authoritative cart payload returned by the backend
// (`serialize_cart`) into the client-side CartItemType shape. The backend is
// the single source of truth for price, discount and stock.
export const mapServerCartItems = (serverItems: unknown): CartItemType[] => {
    if (!Array.isArray(serverItems)) return [];
    return serverItems
        .filter((raw): raw is Record<string, unknown> => !!raw && typeof raw === "object")
        .map((raw) => {
            const price = toNullableNumber(raw.price) ?? 0;
            const qty = toNullableNumber(raw.qty) ?? 1;
            return {
                image: (raw.image as string | null) ?? null,
                name: String(raw.name ?? ""),
                slug: String(raw.slug ?? ""),
                price,
                disc_price: toNullableNumber(raw.disc_price),
                qty: Math.max(1, Math.trunc(qty)),
                stock: toNullableNumber(raw.stock),
            };
        })
        .filter((item) => item.slug !== "");
};

// Defensive hydration of the guest cart from localStorage. Tolerates malformed
// JSON, missing fields, bad quantities, and duplicate lines without throwing.
export const sanitizeStoredCartItems = (raw: unknown): CartItemType[] => {
    if (!Array.isArray(raw)) return [];
    const seen = new Set<string>();
    const items: CartItemType[] = [];

    for (const entry of raw) {
        if (!entry || typeof entry !== "object") continue;
        const candidate = entry as Record<string, unknown>;
        const slug = typeof candidate.slug === "string" ? candidate.slug.trim() : "";
        if (!slug || seen.has(slug)) continue;

        const price = toNullableNumber(candidate.price);
        if (price === null) continue;

        const parsedQty = toNullableNumber(candidate.qty) ?? 1;
        const qty = Math.max(1, Math.trunc(parsedQty));
        const stock = toNullableNumber(candidate.stock);
        const clampedQty = stock !== null && stock > 0 ? Math.min(qty, stock) : qty;

        seen.add(slug);
        items.push({
            image: typeof candidate.image === "string" ? candidate.image : null,
            name: typeof candidate.name === "string" ? candidate.name : "",
            slug,
            price,
            disc_price: toNullableNumber(candidate.disc_price),
            qty: clampedQty,
            stock,
        });
    }

    return items;
};

// Minimal payload the backend needs to merge a guest cart on login: it
// recalculates everything else from the product catalogue.
export const serializeCartItemsForSync = (items: CartItemType[]) =>
    sanitizeStoredCartItems(items).map((item) => ({ slug: item.slug, qty: item.qty }));
